// ControlsPage.tsx — Section VIII: Controls.
//
// "Manual interventions. Every action is recorded in the Audit Log."
// (Lore-approved CT-03)
//
// Seven admin-action panels:
//   1. Auto-Top-Up panel (read + 4 setters + manual trigger)
//   2. Manual Cycle Top-Up (form + confirmation modal)
//   3. Force Rename (search + validate + confirmation)
//   4. Banned Word Management (list + add + remove)
//   5. Stale Room Force-Finish (input + confirmation)
//   6. Subscription Override (deactivate + extend)
//   7. Admin Allowlist View (read-only)
//
// All writes are confirmation-gated. No exclamation marks. Cinzel/Cormorant.
// Canister enforces admin gate; frontend is UX hardening only.

import React, { useCallback, useEffect, useRef, useState } from 'react';
import type { Identity } from '@dfinity/agent';
import ConfirmModal from '../components/ConfirmModal';
import { ToastHost, useToast } from '../components/Toast';
import { useAutoTopUp } from '../hooks/useAutoTopUp';
import { useBannedWords } from '../hooks/useBannedWords';
import { useIcpTopUp } from '../hooks/useIcpTopUp';
import { usePrivacyRights, PRIVACY_ACTION_LABELS } from '../hooks/usePrivacyRights';
import type { PrivacyActionType, StripeCustomerMatch } from '../hooks/usePrivacyRights';
import { useStaleRooms } from '../hooks/useStaleRooms';
import type { StaleRoomInfo } from '../hooks/useStaleRooms';
import { createAdminAgent, createAdminActor } from '../lib/adminAgent';
import { DATA_RIGHTS_COPY } from '../lib/dataRightsCopy';

interface ControlsPageProps {
  identity: Identity | null;
}

// Format bigint cycles as human T/G string
function fmtCycles(n: bigint): string {
  if (n >= 1_000_000_000_000n) {
    const t = Number(n) / 1e12;
    return t.toFixed(t % 1 === 0 ? 0 : 2) + 'T';
  }
  if (n >= 1_000_000_000n) {
    return (Number(n) / 1e9).toFixed(2) + 'G';
  }
  return n.toString();
}

// ---------------------------------------------------------------------------
// Auto-Top-Up Panel
// ---------------------------------------------------------------------------

function AutoTopUpPanel({ identity }: { identity: Identity | null }) {
  const {
    config,
    isLoading,
    isMutating,
    mutationError,
    lastTopUpSummary,
    refresh,
    setEnabled,
    setThreshold,
    setAmount,
    setDailyCap,
    runTopUpCheck,
  } = useAutoTopUp(identity);

  const { messages, toast, dismiss } = useToast();

  // Modal state: which field is being edited?
  type ModalKind = 'threshold' | 'amount' | 'dailyCap' | 'enabled' | 'trigger' | null;
  const [modalKind, setModalKind] = useState<ModalKind>(null);
  const [inputValue, setInputValue] = useState('');
  const [pendingEnabled, setPendingEnabled] = useState(false);

  const openNumberModal = (kind: 'threshold' | 'amount' | 'dailyCap', currentVal: bigint) => {
    setInputValue(fmtCycles(currentVal).replace(/[TG]/, ''));
    setModalKind(kind);
  };

  const handleConfirm = async () => {
    if (!config) return;
    const kind = modalKind;
    setModalKind(null);
    try {
      if (kind === 'threshold') {
        await setThreshold(BigInt(Math.round(parseFloat(inputValue) * 1e12)));
        toast('ok', 'Auto-top-up threshold updated.');
      } else if (kind === 'amount') {
        await setAmount(BigInt(Math.round(parseFloat(inputValue) * 1e12)));
        toast('ok', 'Auto-top-up amount updated.');
      } else if (kind === 'dailyCap') {
        await setDailyCap(BigInt(Math.round(parseFloat(inputValue) * 1e12)));
        toast('ok', 'Daily cap updated.');
      } else if (kind === 'enabled') {
        await setEnabled(pendingEnabled);
        toast('ok', pendingEnabled ? 'Auto-top-up enabled.' : 'Auto-top-up disabled.');
      } else if (kind === 'trigger') {
        const summary = await runTopUpCheck();
        if (summary) {
          toast('ok', `Top-up check complete. ${summary.triggered} canister(s) topped up.`);
        } else {
          toast('err', 'Top-up check failed. See error details.');
        }
      }
    } catch {
      toast('err', 'Action failed. See error details.');
    }
  };

  return (
    <div className="panel">
      <ToastHost messages={messages} onDismiss={dismiss} />
      <p className="panel-title">Auto Top-Up</p>
      <p className="panel-subtitle">
        Fires when any canister falls below threshold. Daily cap is enforced.
      </p>

      {isLoading && <p className="card-caption">Loading configuration.</p>}
      {mutationError && <div className="inline-error">{mutationError}</div>}

      {config && (
        <>
          <div className="kv">
            <span className="kv-name">Status</span>
            <span className="kv-val">{config.enabled ? 'enabled' : 'disabled (kill switch)'}</span>
          </div>
          <div className="kv">
            <span className="kv-name">Threshold</span>
            <span className="kv-val">{fmtCycles(config.threshold)} cycles</span>
          </div>
          <div className="kv">
            <span className="kv-name">Top-Up Amount</span>
            <span className="kv-val">{fmtCycles(config.amount)} cycles</span>
          </div>
          <div className="kv">
            <span className="kv-name">Daily Cap</span>
            <span className="kv-val">{fmtCycles(config.dailyCap)} cycles</span>
          </div>
          <div className="kv">
            <span className="kv-name">Cap Used Today</span>
            <span className="kv-val">{fmtCycles(config.dailyUsed)} cycles</span>
          </div>
          <div className="kv">
            <span className="kv-name">Cap Remaining</span>
            <span className="kv-val">{fmtCycles(config.capRemaining)} cycles</span>
          </div>

          <div className="controls-row" style={{ marginTop: 'var(--s-md)', display: 'flex', flexWrap: 'wrap', gap: 'var(--s-sm)' }}>
            <button
              className="btn btn-secondary"
              onClick={() => { setPendingEnabled(!config.enabled); setModalKind('enabled'); }}
              disabled={isMutating}
            >
              {config.enabled ? 'Disable' : 'Enable'} Auto-Top-Up
            </button>
            <button
              className="btn btn-secondary"
              onClick={() => openNumberModal('threshold', config.threshold)}
              disabled={isMutating}
            >
              Set Threshold
            </button>
            <button
              className="btn btn-secondary"
              onClick={() => openNumberModal('amount', config.amount)}
              disabled={isMutating}
            >
              Set Amount
            </button>
            <button
              className="btn btn-secondary"
              onClick={() => openNumberModal('dailyCap', config.dailyCap)}
              disabled={isMutating}
            >
              Set Daily Cap
            </button>
            <button
              className="btn"
              onClick={() => setModalKind('trigger')}
              disabled={isMutating || !config.enabled}
            >
              Run Auto-Top-Up Check Now
            </button>
            <button className="btn btn-secondary" onClick={refresh} disabled={isLoading}>
              Refresh
            </button>
          </div>

          {lastTopUpSummary && (
            <div style={{ marginTop: 'var(--s-md)' }}>
              <p className="panel-subtitle">
                Last check: {Number(lastTopUpSummary.triggered)} canister(s) topped up,{' '}
                {fmtCycles(lastTopUpSummary.cyclesDeposited)} deposited.
                Cap remaining: {fmtCycles(lastTopUpSummary.capRemaining)}.
              </p>
              {lastTopUpSummary.skipped.length > 0 && (
                <p className="card-caption">Skipped: {lastTopUpSummary.skipped.join(' · ')}</p>
              )}
            </div>
          )}
        </>
      )}

      {/* Confirmation modals */}
      {modalKind === 'enabled' && (
        <ConfirmModal
          title={pendingEnabled ? 'Enable Auto-Top-Up' : 'Disable Auto-Top-Up (Kill Switch)'}
          body={pendingEnabled
            ? 'Auto-top-up will resume on the next check cycle.'
            : 'Auto-top-up will not fire until re-enabled. Canisters may run dry if manually unmonitored.'}
          confirmLabel={pendingEnabled ? 'Enable' : 'Disable'}
          isDestructive={!pendingEnabled}
          isLoading={isMutating}
          onConfirm={handleConfirm}
          onCancel={() => setModalKind(null)}
        />
      )}
      {(modalKind === 'threshold' || modalKind === 'amount' || modalKind === 'dailyCap') && (
        <ConfirmModal
          title={
            modalKind === 'threshold' ? 'Update Auto-Top-Up Threshold' :
            modalKind === 'amount' ? 'Update Auto-Top-Up Amount' :
            'Update Daily Top-Up Cap'
          }
          body={
            <div>
              <p className="modal-body-text" style={{ marginBottom: 'var(--s-md)' }}>
                {modalKind === 'threshold' ? 'New threshold in T cycles (e.g. 1 = 1T):' :
                 modalKind === 'amount' ? 'New top-up amount in T cycles (e.g. 2 = 2T):' :
                 'New daily cap in T cycles (e.g. 10 = 10T):'}
              </p>
              <div className="field">
                <input
                  type="number"
                  min="0.1"
                  step="0.1"
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  style={{ fontFamily: 'var(--font-ui)', fontSize: '16px', color: 'var(--ivory)', background: 'rgba(0,0,0,0.4)', border: '1px solid var(--gold-stroke)', padding: '10px 12px' }}
                  autoFocus
                />
              </div>
            </div>
          }
          confirmLabel="Update"
          isLoading={isMutating}
          onConfirm={handleConfirm}
          onCancel={() => setModalKind(null)}
        />
      )}
      {modalKind === 'trigger' && (
        <ConfirmModal
          title="Run Auto-Top-Up Check Now"
          body="Triggers an immediate sweep across all wired canisters. Deposits will be counted against today's daily cap."
          confirmLabel="Run Check"
          isLoading={isMutating}
          onConfirm={handleConfirm}
          onCancel={() => setModalKind(null)}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Manual Cycle Top-Up Panel
// ---------------------------------------------------------------------------

// Mainnet canister principals — auto-populated when the operator picks a
// canister from the dropdown so the principal field is never blank
// (Jerry's 2026-05-22 UX fix). Override-by-typing still works because the
// principal text input is fully editable; auto-populate is one-shot.
//
// Source: memory/mainnet_canister_ids.md + dfx canister id at deploy time.
// dominus_admin is intentionally omitted — admins cannot top up themselves
// via the manual flow (use IcpTopUpPanel for the treasury canister).
const CANISTER_OPTIONS: ReadonlyArray<{ label: string; key: string; principal: string }> = [
  { label: 'auth_canister',         key: 'auth',         principal: 'yuyfj-5aaaa-aaaam-ajbia-cai' },
  { label: 'lobby_canister',        key: 'lobby',        principal: 'ytzd5-qyaaa-aaaam-ajbiq-cai' },
  { label: 'game_canister',         key: 'game',         principal: 'y22ib-gqaaa-aaaam-ajbja-cai' },
  { label: 'subscription_canister', key: 'subscription', principal: '2jflb-oiaaa-aaaam-ajbhq-cai' },
  { label: 'stats_canister',        key: 'stats',        principal: 'y53ov-liaaa-aaaam-ajbjq-cai' },
  { label: 'dominus_frontend',      key: 'frontend',     principal: 'yp5zm-hyaaa-aaaam-ajbkq-cai' },
];

function ManualTopUpPanel({ identity }: { identity: Identity | null }) {
  const { messages, toast, dismiss } = useToast();
  const [targetLabel, setTargetLabel] = useState(CANISTER_OPTIONS[0].label);
  // Pre-populate the principal input with the first option's canister ID
  // so the "Send Top-Up" button is enabled the instant an amount is entered
  // (no more silent-disabled-button gap from Jerry's 2026-05-22 report).
  const [principalText, setPrincipalText] = useState(CANISTER_OPTIONS[0].principal);
  const [amountT, setAmountT] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [isMutating, setIsMutating] = useState(false);

  const handleConfirm = async () => {
    if (!identity) return;
    setShowModal(false);
    setIsMutating(true);
    try {
      // Validate principal format before calling the canister — Principal.fromText
      // throws on malformed input; we surface that as a toast rather than crashing.
      const { Principal } = await import('@dfinity/principal');
      let targetPrincipal: ReturnType<typeof Principal.fromText>;
      try {
        targetPrincipal = Principal.fromText(principalText.trim());
      } catch {
        toast('err', 'Invalid principal format.');
        setIsMutating(false);
        return;
      }
      // Convert T cycles (decimal) to bigint. parseFloat supports "2.5" → 2.5 → 2_500_000_000_000n.
      const amountCycles = BigInt(Math.round(parseFloat(amountT) * 1e12));
      const agent = await createAdminAgent(identity);
      const actor = createAdminActor(agent);
      const result = await actor.admin_manual_top_up_canister(targetPrincipal, amountCycles);
      if ('err' in result) {
        toast('err', `Top-up failed: ${result.err}`);
      } else {
        const deposited = result.ok;
        const depositedT = (Number(deposited) / 1e12).toFixed(2);
        toast('ok', `Deposited ${depositedT}T cycles into ${targetLabel}.`);
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Top-up failed.';
      toast('err', msg);
    } finally {
      setIsMutating(false);
    }
  };

  // Amount must be a positive number; principal field must be non-empty.
  const isValid =
    amountT.trim() !== '' &&
    parseFloat(amountT) > 0 &&
    principalText.trim().length > 0;

  return (
    <div className="panel">
      <ToastHost messages={messages} onDismiss={dismiss} />
      <p className="panel-title">Manual Cycle Top-Up</p>
      <p className="panel-subtitle">
        Refill a single canister outside the auto-top-up schedule.
      </p>

      <div className="field">
        <label className="field-label" htmlFor="topup-canister">Canister</label>
        <select
          id="topup-canister"
          value={targetLabel}
          onChange={(e) => {
            const nextLabel = e.target.value;
            setTargetLabel(nextLabel);
            // Auto-populate the principal field with the selected canister's
            // mainnet ID so the operator never has to paste it manually.
            // Editable by typing — auto-populate is a one-shot convenience.
            const match = CANISTER_OPTIONS.find((o) => o.label === nextLabel);
            if (match) setPrincipalText(match.principal);
          }}
        >
          {CANISTER_OPTIONS.map((o) => (
            <option key={o.key} value={o.label}>{o.label}</option>
          ))}
        </select>
      </div>

      <div className="field">
        <label className="field-label" htmlFor="topup-principal">Target Principal</label>
        <input
          id="topup-principal"
          type="text"
          placeholder="aaaaa-aa…"
          value={principalText}
          onChange={(e) => setPrincipalText(e.target.value)}
        />
        <span className="field-help">
          Auto-populated from the canister above. Override only if you know the principal differs.
        </span>
      </div>

      <div className="field">
        <label className="field-label" htmlFor="topup-amount">Amount (T cycles)</label>
        <input
          id="topup-amount"
          type="number"
          min="0.1"
          step="0.1"
          placeholder="e.g. 2 for 2T cycles"
          value={amountT}
          onChange={(e) => setAmountT(e.target.value)}
        />
        <span className="field-help">Drawn from the cycles ledger. Counted against today's cap.</span>
      </div>

      <button
        className="btn"
        onClick={() => setShowModal(true)}
        disabled={!isValid || isMutating}
      >
        {isMutating ? 'Sending Top-Up' : 'Send Top-Up'}
      </button>
      {!isValid && !isMutating && (
        <span className="btn-disabled-caption">
          {principalText.trim().length === 0
            ? 'Select a canister to populate the principal, then enter an amount.'
            : amountT.trim() === '' || parseFloat(amountT) <= 0
              ? 'Enter an amount in T cycles to enable.'
              : 'Enter principal and amount to enable.'}
        </span>
      )}

      {showModal && (
        <ConfirmModal
          title="Confirm Manual Top-Up"
          body={`Deposit ${amountT}T cycles into ${targetLabel}? Amount is counted against today's daily cap.`}
          confirmLabel="Send Top-Up"
          isLoading={isMutating}
          onConfirm={handleConfirm}
          onCancel={() => setShowModal(false)}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// ICP -> Cycles Top-Up Panel (Atlas, 2026-05-22)
//
// Two-step in-admin flow that replaces the legacy NNS + cycles-wallet +
// dfx-wallet-send path:
//   1. Send ICP from NNS account to the displayed AccountIdentifier.
//   2. Paste the block index returned by NNS. Click "Notify CMC".
// CMC verifies, burns the ICP, and mints cycles directly into dominus_admin.
// ---------------------------------------------------------------------------

function IcpTopUpPanel({ identity }: { identity: Identity | null }) {
  const {
    accountId,
    isLoadingAccountId,
    accountIdError,
    isMutating,
    lastResult,
    error,
    triggerNotify,
    refreshAccountId,
  } = useIcpTopUp(identity);

  const { messages, toast, dismiss } = useToast();
  const [blockIndexText, setBlockIndexText] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [copyState, setCopyState] = useState<'idle' | 'copied' | 'error'>('idle');
  const copyTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Cleanup the copy-state fade timer on unmount.
  useEffect(() => {
    return () => {
      if (copyTimerRef.current !== null) clearTimeout(copyTimerRef.current);
    };
  }, []);

  const handleCopy = async () => {
    if (!accountId) return;
    try {
      await navigator.clipboard.writeText(accountId);
      setCopyState('copied');
    } catch {
      setCopyState('error');
    }
    if (copyTimerRef.current !== null) clearTimeout(copyTimerRef.current);
    copyTimerRef.current = setTimeout(() => setCopyState('idle'), 2200);
  };

  const parsedBlockIndex = (() => {
    const trimmed = blockIndexText.trim();
    if (trimmed === '') return null;
    if (!/^\d+$/.test(trimmed)) return null;
    try {
      const n = BigInt(trimmed);
      if (n < 0n) return null;
      // CMC block indices fit in Nat64 (uint64 max = 2^64 - 1). Defensive check.
      if (n > 0xffffffffffffffffn) return null;
      return n;
    } catch {
      return null;
    }
  })();

  const isValid = parsedBlockIndex !== null && !isMutating && accountId !== null;

  const handleConfirm = async () => {
    if (parsedBlockIndex === null) return;
    setShowModal(false);
    const ok = await triggerNotify(parsedBlockIndex);
    if (ok) {
      setBlockIndexText('');
      toast('ok', 'Treasury topped up.');
    }
    // Errors surface via the `error` state below — no toast needed.
  };

  return (
    <div className="panel">
      <ToastHost messages={messages} onDismiss={dismiss} />
      <p className="panel-title">Top Up Treasury with ICP</p>
      <p className="panel-subtitle">
        Convert ICP into cycles deposited directly to dominus_admin. Bypasses the cycles wallet.
      </p>

      <div className="field">
        <label className="field-label" htmlFor="icp-topup-accountid">Send ICP to this address</label>
        {isLoadingAccountId ? (
          <div className="icp-topup-accountid is-loading" id="icp-topup-accountid">
            Loading account ID…
          </div>
        ) : accountIdError ? (
          <div className="icp-topup-error" role="alert">
            Could not load the AccountIdentifier: {accountIdError}.{' '}
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => { refreshAccountId(); }}
            >
              Retry
            </button>
          </div>
        ) : (
          <div className="icp-topup-accountid" id="icp-topup-accountid" aria-label="ICP AccountIdentifier">
            {accountId ?? ''}
          </div>
        )}
        <div className="icp-topup-copy-row">
          <button
            type="button"
            className="btn btn-secondary"
            onClick={handleCopy}
            disabled={!accountId || isLoadingAccountId}
          >
            Copy
          </button>
          {copyState === 'copied' && (
            <span className="icp-topup-copy-state" aria-live="polite">Copied to clipboard.</span>
          )}
          {copyState === 'error' && (
            <span className="icp-topup-copy-state" aria-live="polite">Copy failed — select the text manually.</span>
          )}
        </div>
        <span className="field-help">
          From your NNS account, send ICP to this address. Note the block index that NNS returns after confirmation.
        </span>
      </div>

      <div className="field">
        <label className="field-label" htmlFor="icp-topup-blockindex">Block index from the NNS transaction</label>
        <input
          id="icp-topup-blockindex"
          type="text"
          inputMode="numeric"
          pattern="[0-9]*"
          placeholder="e.g. 12345678"
          value={blockIndexText}
          onChange={(e) => setBlockIndexText(e.target.value)}
        />
        <span className="field-help">
          Paste the block index from your NNS confirmation screen. The CMC verifies the transfer before minting cycles.
        </span>
      </div>

      <button
        className="btn"
        onClick={() => setShowModal(true)}
        disabled={!isValid}
      >
        {isMutating ? 'Notifying CMC…' : 'Notify CMC and Top Up'}
      </button>
      {!isValid && !isMutating && (
        <span className="btn-disabled-caption">
          {accountId === null
            ? 'Waiting for the AccountIdentifier to load.'
            : blockIndexText.trim() === ''
              ? 'Paste the NNS block index to enable.'
              : 'Block index must be a positive integer.'}
        </span>
      )}

      {lastResult !== null && (
        <div className="icp-topup-success" role="status">
          Treasury topped up with {(Number(lastResult.cyclesMinted) / 1e12).toFixed(2)}T cycles.
        </div>
      )}
      {error !== null && (
        <div className="icp-topup-error" role="alert">
          {error}
        </div>
      )}

      {showModal && parsedBlockIndex !== null && (
        <ConfirmModal
          title="Confirm CMC notification"
          body={
            `Notify CMC for block index ${parsedBlockIndex.toString()}? ` +
            'This converts your ICP transfer to cycles and deposits them into dominus_admin.'
          }
          confirmLabel="Notify CMC"
          isLoading={isMutating}
          onConfirm={handleConfirm}
          onCancel={() => setShowModal(false)}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Force Rename Panel
// ---------------------------------------------------------------------------

function ForceRenamePanel({ identity }: { identity: Identity | null }) {
  const { messages, toast, dismiss } = useToast();
  const [principalText, setPrincipalText] = useState('');
  const [newName, setNewName] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [isMutating, setIsMutating] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);

  const isValid = principalText.trim().length > 0 && newName.trim().length >= 3 && newName.trim().length <= 20;

  const handleSubmit = async () => {
    if (!identity || !isValid) return;
    setValidationError(null);

    // Basic client-side validation before showing modal
    const trimmed = newName.trim();
    if (trimmed.length < 3 || trimmed.length > 20) {
      setValidationError('Three to twenty characters.');
      return;
    }
    setShowModal(true);
  };

  const handleConfirm = async () => {
    if (!identity) return;
    setShowModal(false);
    setIsMutating(true);
    try {
      const { Principal } = await import('@dfinity/principal');
      const agent = await createAdminAgent(identity);
      const actor = createAdminActor(agent);
      const target = Principal.fromText(principalText.trim());
      const result = await actor.admin_force_rename(target, newName.trim());
      if ('err' in result) throw new Error(result.err);
      toast('ok', `Display name updated. Audit log entry created.`);
      setPrincipalText('');
      setNewName('');
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Force rename failed.';
      toast('err', msg);
    } finally {
      setIsMutating(false);
    }
  };

  return (
    <div className="panel">
      <ToastHost messages={messages} onDismiss={dismiss} />
      <p className="panel-title">Force Rename Player</p>
      <p className="panel-subtitle">
        Override a display name flagged for content review.
      </p>

      <div className="field">
        <label className="field-label" htmlFor="rename-principal">Principal</label>
        <input
          id="rename-principal"
          type="text"
          placeholder="aaaaa-aa…"
          value={principalText}
          onChange={(e) => setPrincipalText(e.target.value)}
        />
      </div>

      <div className="field">
        <label className="field-label" htmlFor="rename-newname">Replacement Name</label>
        <input
          id="rename-newname"
          type="text"
          placeholder="3–20 characters"
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
          maxLength={20}
        />
        <span className="field-help">Three to twenty characters.</span>
        {validationError && <span className="field-error">{validationError}</span>}
      </div>

      <button
        className="btn"
        onClick={handleSubmit}
        disabled={!isValid || isMutating}
      >
        {isMutating ? 'Applying Rename' : 'Apply Rename'}
      </button>
      {!isValid && !isMutating && (
        <span className="btn-disabled-caption">
          {principalText.trim().length === 0
            ? 'Paste the target principal to enable.'
            : 'Replacement name must be 3 to 20 characters.'}
        </span>
      )}

      {showModal && (
        <ConfirmModal
          title="Confirm Force Rename"
          body={`Rename the player at ${principalText.slice(0, 12)}… to "${newName}"? This action is logged in the Audit Log.`}
          confirmLabel="Apply Rename"
          isDestructive
          isLoading={isMutating}
          onConfirm={handleConfirm}
          onCancel={() => setShowModal(false)}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Banned Word Management Panel
// ---------------------------------------------------------------------------

function BannedWordsPanel({ identity }: { identity: Identity | null }) {
  const { words, isLoading, isMutating, mutationError, addWord, removeWord } = useBannedWords(identity);
  const { messages, toast, dismiss } = useToast();
  const [newWord, setNewWord] = useState('');
  const [wordToRemove, setWordToRemove] = useState<string | null>(null);

  const handleAdd = async () => {
    const trimmed = newWord.trim();
    if (!trimmed) return;
    const ok = await addWord(trimmed);
    if (ok) {
      toast('ok', `"${trimmed}" added to banned list.`);
      setNewWord('');
    } else {
      toast('err', mutationError ?? 'Failed to add word.');
    }
  };

  const handleRemoveConfirm = async () => {
    if (!wordToRemove) return;
    const word = wordToRemove;
    setWordToRemove(null);
    const ok = await removeWord(word);
    if (ok) {
      toast('ok', `"${word}" removed from banned list.`);
    } else {
      toast('err', mutationError ?? 'Failed to remove word.');
    }
  };

  return (
    <div className="panel">
      <ToastHost messages={messages} onDismiss={dismiss} />
      <p className="panel-title">Banned Word List</p>
      <p className="panel-subtitle">
        Substrings filtered from display names and chat. Comma-separated.
      </p>

      {isLoading && <p className="card-caption">Loading.</p>}

      {words.length === 0 && !isLoading ? (
        <p className="card-caption" style={{ marginBottom: 'var(--s-md)' }}>
          No banned substrings. The list is empty.
        </p>
      ) : (
        <div className="banned-word-list">
          {words.map((word) => (
            <div key={word} className="banned-word-row">
              <span className="banned-word-text">{word}</span>
              <button
                className="btn btn-secondary btn-sm"
                onClick={() => setWordToRemove(word)}
                disabled={isMutating}
              >
                Remove
              </button>
            </div>
          ))}
        </div>
      )}

      <div className="field" style={{ marginTop: 'var(--s-md)' }}>
        <label className="field-label" htmlFor="banned-substrings">Banned substrings</label>
        <input
          id="banned-substrings"
          type="text"
          placeholder="e.g. slur, badword"
          value={newWord}
          onChange={(e) => setNewWord(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') handleAdd(); }}
        />
      </div>
      <button
        className="btn"
        onClick={handleAdd}
        disabled={!newWord.trim() || isMutating}
      >
        {isMutating ? 'Saving' : 'Save List'}
      </button>
      {!newWord.trim() && !isMutating && (
        <span className="btn-disabled-caption">
          Enter a substring above to enable.
        </span>
      )}

      {wordToRemove && (
        <ConfirmModal
          title="Remove Banned Word"
          body={`Remove "${wordToRemove}" from the banned list? Display names and chat containing this substring will no longer be filtered.`}
          confirmLabel="Remove"
          isDestructive
          isLoading={isMutating}
          onConfirm={handleRemoveConfirm}
          onCancel={() => setWordToRemove(null)}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Stale Room Force-Finish Panel
// ---------------------------------------------------------------------------

function ForceFinishRoomPanel({ identity }: { identity: Identity | null }) {
  const { messages, toast, dismiss } = useToast();
  const [roomId, setRoomId] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [isMutating, setIsMutating] = useState(false);

  const handleConfirm = async () => {
    if (!identity || !roomId.trim()) return;
    setShowModal(false);
    setIsMutating(true);
    try {
      const agent = await createAdminAgent(identity);
      const actor = createAdminActor(agent);
      const result = await actor.admin_force_finish_room(roomId.trim());
      if ('err' in result) throw new Error(result.err);
      toast('ok', `Room ${roomId.trim()} marked finished. Players disconnected.`);
      setRoomId('');
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Force finish failed.';
      toast('err', msg);
    } finally {
      setIsMutating(false);
    }
  };

  return (
    <div className="panel">
      <ToastHost messages={messages} onDismiss={dismiss} />
      <p className="panel-title">Mark Stale Room Finished</p>
      <p className="panel-subtitle">
        Forces a stuck or orphaned room into a terminal state. Last resort.
      </p>

      <div className="field">
        <label className="field-label" htmlFor="room-id">Room ID or Friend Code</label>
        <input
          id="room-id"
          type="text"
          placeholder="Room ID or friend code"
          value={roomId}
          onChange={(e) => setRoomId(e.target.value)}
        />
      </div>

      <button
        className="btn"
        onClick={() => setShowModal(true)}
        disabled={!roomId.trim() || isMutating}
        style={{ borderColor: 'var(--gold-m)', color: 'var(--gold-m)' }}
      >
        {isMutating ? 'Forcing Finish' : 'Force Finish'}
      </button>
      {!roomId.trim() && !isMutating && (
        <span className="btn-disabled-caption">
          Enter a room ID or friend code to enable.
        </span>
      )}

      {showModal && (
        <ConfirmModal
          title="Force-Finish Room"
          body={`Force-finish room "${roomId.trim()}"? Players in the room will be disconnected.`}
          confirmLabel="Force Finish"
          isDestructive
          isLoading={isMutating}
          onConfirm={handleConfirm}
          onCancel={() => setShowModal(false)}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Subscription Override Panel
// ---------------------------------------------------------------------------

function SubscriptionOverridePanel({ identity }: { identity: Identity | null }) {
  const { messages, toast, dismiss } = useToast();
  const [principalText, setPrincipalText] = useState('');
  const [reason, setReason] = useState('');
  const [days, setDays] = useState('');
  const [action, setAction] = useState<'deactivate' | 'extend' | null>(null);
  const [isMutating, setIsMutating] = useState(false);

  const handleConfirm = async () => {
    if (!identity || !action) return;
    const confirmedAction = action;
    setAction(null);
    setIsMutating(true);
    try {
      const { Principal } = await import('@dfinity/principal');
      let targetPrincipal: ReturnType<typeof Principal.fromText>;
      try {
        targetPrincipal = Principal.fromText(principalText.trim());
      } catch {
        toast('err', 'Invalid principal format.');
        setIsMutating(false);
        return;
      }

      // Build the SubscriptionOverrideAction variant. Exactly one branch present per call.
      // Motoko: #deactivate(Text) | #extend(Nat) → TS: { deactivate: string } | { extend: bigint }
      const overrideAction = confirmedAction === 'deactivate'
        ? { deactivate: reason.trim() }
        : { extend: BigInt(parseInt(days, 10)) };

      const agent = await createAdminAgent(identity);
      const actor = createAdminActor(agent);
      const result = await actor.admin_subscription_override(targetPrincipal, overrideAction);

      if ('err' in result) {
        toast('err', `Override failed: ${result.err}`);
      } else {
        if (confirmedAction === 'deactivate') {
          toast('ok', 'Subscription deactivated. Audit log records the action.');
        } else {
          toast('ok', `Subscription extended by ${days} days. Audit log records the action.`);
        }
        setPrincipalText('');
        setReason('');
        setDays('');
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Override failed.';
      toast('err', msg);
    } finally {
      setIsMutating(false);
    }
  };

  return (
    <div className="panel">
      <ToastHost messages={messages} onDismiss={dismiss} />
      <p className="panel-title">Refund Subscription</p>
      <p className="panel-subtitle">
        Issues a Stripe refund and revokes access at the billing date.
      </p>

      <div className="field">
        <label className="field-label" htmlFor="sub-principal">Member Principal</label>
        <input
          id="sub-principal"
          type="text"
          placeholder="aaaaa-aa…"
          value={principalText}
          onChange={(e) => setPrincipalText(e.target.value)}
        />
      </div>

      <div style={{ display: 'flex', gap: 'var(--s-sm)', flexWrap: 'wrap', marginTop: 'var(--s-md)' }}>
        <div className="field" style={{ flex: '1', minWidth: '200px', marginBottom: 0 }}>
          <label className="field-label" htmlFor="sub-reason">Reason (for deactivation)</label>
          <input
            id="sub-reason"
            type="text"
            placeholder="Reason text required"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
          />
        </div>
        <div className="field" style={{ flex: '1', minWidth: '120px', marginBottom: 0 }}>
          <label className="field-label" htmlFor="sub-days">Days to Extend</label>
          <input
            id="sub-days"
            type="number"
            min="1"
            placeholder="e.g. 30"
            value={days}
            onChange={(e) => setDays(e.target.value)}
          />
        </div>
      </div>

      <div style={{ display: 'flex', gap: 'var(--s-sm)', marginTop: 'var(--s-md)', flexWrap: 'wrap' }}>
        <button
          className="btn"
          onClick={() => setAction('extend')}
          disabled={!principalText.trim() || !days || parseInt(days, 10) < 1 || isMutating}
        >
          {isMutating && action === 'extend' ? 'Extending' : 'Force Extend'}
        </button>
        <button
          className="btn"
          onClick={() => setAction('deactivate')}
          disabled={!principalText.trim() || !reason.trim() || isMutating}
          style={{ borderColor: 'var(--gold-m)', color: 'var(--gold-m)' }}
        >
          {isMutating && action === 'deactivate' ? 'Issuing Refund' : 'Issue Refund'}
        </button>
      </div>
      {!isMutating && (!principalText.trim() || (!days && !reason.trim())) && (
        <span className="btn-disabled-caption">
          {principalText.trim().length === 0
            ? 'Paste the member principal to enable.'
            : 'Enter a reason for refund, or days to extend, to enable.'}
        </span>
      )}

      {action === 'deactivate' && (
        <ConfirmModal
          title="Deactivate Subscription"
          body={`Deactivate subscription for ${principalText.slice(0, 12)}…? Reason: "${reason}". This action is logged in the Audit Log. Stripe-side refund must be issued separately in the Stripe dashboard.`}
          confirmLabel="Deactivate"
          isDestructive
          isLoading={isMutating}
          onConfirm={handleConfirm}
          onCancel={() => setAction(null)}
        />
      )}
      {action === 'extend' && (
        <ConfirmModal
          title="Extend Subscription"
          body={`Extend subscription for ${principalText.slice(0, 12)}… by ${days} day(s)? This action is logged in the Audit Log.`}
          confirmLabel="Force Extend"
          isLoading={isMutating}
          onConfirm={handleConfirm}
          onCancel={() => setAction(null)}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Data Rights Panel
// ---------------------------------------------------------------------------
//
// Three sub-panels fulfilling the privacy-page "Your Rights" commitments:
//   A — Look up Stripe customer by email
//   B — Export user data as JSON (triggers browser download)
//   C — Log a privacy event manually in the Audit Log
//
// Wired to:
//   - Cash's Worker route: GET /admin/lookup-stripe-by-email
//   - Koda's canister methods: admin_export_user_data,
//     admin_lookup_principal_by_stripe_customer, admin_log_privacy_event

const PRIVACY_ACTIONS = Object.keys(PRIVACY_ACTION_LABELS) as PrivacyActionType[];

// BigInt-safe JSON serializer — BigInt values in UserDataBundle are not
// natively JSON-serializable; this replacer converts them to strings so the
// downloaded file is valid JSON that roundtrips without data loss.
function bigIntReplacer(_key: string, value: unknown): unknown {
  return typeof value === 'bigint' ? value.toString() : value;
}

function maskEmail(email: string): string {
  const [local, domain] = email.split('@');
  if (!local || !domain) return email;
  const visible = local[0] ?? '';
  return `${visible}${'*'.repeat(Math.max(local.length - 1, 4))}@${domain}`;
}

function fmtUnixDate(seconds: number): string {
  return new Date(seconds * 1000).toLocaleDateString('en-US', {
    year: 'numeric', month: 'short', day: 'numeric',
  });
}

function DataRightsPanel({ identity }: { identity: Identity | null }) {
  const {
    isLookingUpStripe, stripeError,
    isResolvingPrincipal, resolveError,
    isExporting, exportError,
    isLoggingEvent, logError,
    lookupStripeByEmail,
    lookupPrincipalByStripe,
    exportUserData,
    logPrivacyEvent,
  } = usePrivacyRights(identity);

  const { messages, toast, dismiss } = useToast();

  // Sub-panel A state
  const [emailInput, setEmailInput] = useState('');
  const [stripeMatches, setStripeMatches] = useState<StripeCustomerMatch[]>([]);
  const [didSearch, setDidSearch] = useState(false);

  // Sub-panel B state
  const [principalInput, setPrincipalInput] = useState('');
  const [showExportConfirm, setShowExportConfirm] = useState(false);

  // Sub-panel C state
  const [eventType, setEventType] = useState<PrivacyActionType>('admin_data_access_request_received');
  const [eventPrincipal, setEventPrincipal] = useState('');
  const [eventDetails, setEventDetails] = useState('');

  // ---- Sub-panel A handlers ----

  const handleLookupStripe = async () => {
    if (!emailInput.trim()) return;
    setDidSearch(false);
    setStripeMatches([]);
    const matches = await lookupStripeByEmail(emailInput);
    setStripeMatches(matches);
    setDidSearch(true);
  };

  const handleResolvePrincipal = async (customerId: string) => {
    const principal = await lookupPrincipalByStripe(customerId);
    if (principal !== null) {
      setPrincipalInput(principal.toText());
      toast('ok', 'Principal resolved. Review and export from Sub-panel B.');
    } else {
      toast('ok', DATA_RIGHTS_COPY.resolveNotLinked);
    }
  };

  // ---- Sub-panel B handlers ----

  const handleExportConfirm = async () => {
    setShowExportConfirm(false);
    if (!principalInput.trim()) return;

    const { Principal } = await import('@dfinity/principal');
    let target: ReturnType<typeof Principal.fromText>;
    try {
      target = Principal.fromText(principalInput.trim());
    } catch {
      toast('err', 'Invalid principal format.');
      return;
    }

    const bundle = await exportUserData(target);
    if (!bundle) return; // exportError already set

    // Trigger browser download as JSON file.
    const shortPrincipal = principalInput.trim().slice(0, 5);
    const date = new Date().toISOString().slice(0, 10); // YYYY-MM-DD
    const filename = `dominus-user-data-${shortPrincipal}-${date}.json`;
    const json = JSON.stringify(bundle, bigIntReplacer, 2);
    const blob = new Blob([json], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);

    toast('ok', DATA_RIGHTS_COPY.exportSuccess);
  };

  // ---- Sub-panel C handlers ----

  const handleLogEvent = async () => {
    const target = eventPrincipal.trim() || null;
    const ok = await logPrivacyEvent(eventType, target, eventDetails);
    if (ok) {
      toast('ok', DATA_RIGHTS_COPY.logSuccess);
      setEventPrincipal('');
      setEventDetails('');
    }
  };

  const detailsRemaining = 1000 - eventDetails.length;

  return (
    <div className="panel" style={{ gridColumn: '1 / -1' }}>
      <ToastHost messages={messages} onDismiss={dismiss} />
      <p className="panel-title">{DATA_RIGHTS_COPY.sectionTitle}</p>
      <p className="panel-subtitle">{DATA_RIGHTS_COPY.sectionSubtitle}</p>

      {/* Divider between sub-panels */}
      <div style={{ display: 'grid', gap: 'var(--s-lg)', marginTop: 'var(--s-lg)' }}>

        {/* ── Sub-panel A: Look up user by email ── */}
        <div className="subpanel">
          <p className="subpanel-title">A — {DATA_RIGHTS_COPY.lookupTitle}</p>
          <p className="panel-subtitle">{DATA_RIGHTS_COPY.lookupSubtitle}</p>

          <div className="field" style={{ marginTop: 'var(--s-md)' }}>
            <label className="field-label" htmlFor="dr-email">{DATA_RIGHTS_COPY.emailLabel}</label>
            <input
              id="dr-email"
              type="email"
              placeholder={DATA_RIGHTS_COPY.emailPlaceholder}
              value={emailInput}
              onChange={(e) => setEmailInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') handleLookupStripe(); }}
            />
          </div>

          <button
            className="btn"
            onClick={handleLookupStripe}
            disabled={!emailInput.trim() || isLookingUpStripe}
          >
            {isLookingUpStripe ? DATA_RIGHTS_COPY.lookupLoading : DATA_RIGHTS_COPY.lookupButton}
          </button>
          {!emailInput.trim() && !isLookingUpStripe && (
            <span className="btn-disabled-caption">Enter an email to enable.</span>
          )}

          {stripeError && <div className="inline-error" style={{ marginTop: 'var(--s-sm)' }}>{stripeError}</div>}

          {didSearch && stripeMatches.length === 0 && !stripeError && (
            <p className="card-caption" style={{ marginTop: 'var(--s-sm)' }}>
              {DATA_RIGHTS_COPY.lookupEmpty}
            </p>
          )}

          {stripeMatches.length > 0 && (
            <div style={{ marginTop: 'var(--s-md)' }}>
              {stripeMatches.map((match) => (
                <div key={match.stripeCustomerId} className="kv" style={{ flexWrap: 'wrap', gap: 'var(--s-sm)', alignItems: 'center' }}>
                  <span className="kv-name" style={{ minWidth: '220px' }}>
                    <span className="mono" style={{ fontSize: '12px', letterSpacing: '0.04em', color: 'var(--ivory-d)' }}>
                      {match.stripeCustomerId}
                    </span>
                    {' '}·{' '}
                    {maskEmail(match.email)}
                    {' '}·{' '}
                    <span style={{ color: 'var(--ivory-d)' }}>{fmtUnixDate(match.created)}</span>
                  </span>
                  <button
                    className="btn btn-secondary btn-sm"
                    onClick={() => handleResolvePrincipal(match.stripeCustomerId)}
                    disabled={isResolvingPrincipal}
                  >
                    {isResolvingPrincipal ? DATA_RIGHTS_COPY.resolving : DATA_RIGHTS_COPY.resolveButton}
                  </button>
                </div>
              ))}
            </div>
          )}

          {resolveError && <div className="inline-error" style={{ marginTop: 'var(--s-sm)' }}>{resolveError}</div>}
        </div>

        {/* ── Sub-panel B: Export user data ── */}
        <div className="subpanel">
          <p className="subpanel-title">B — {DATA_RIGHTS_COPY.exportTitle}</p>
          <p className="panel-subtitle">{DATA_RIGHTS_COPY.exportSubtitle}</p>

          <div className="field" style={{ marginTop: 'var(--s-md)' }}>
            <label className="field-label" htmlFor="dr-principal">{DATA_RIGHTS_COPY.principalLabel}</label>
            <input
              id="dr-principal"
              type="text"
              placeholder={DATA_RIGHTS_COPY.principalPlaceholder}
              value={principalInput}
              onChange={(e) => setPrincipalInput(e.target.value)}
            />
            <span className="field-help">{DATA_RIGHTS_COPY.principalHelp}</span>
          </div>

          <button
            className="btn"
            onClick={() => setShowExportConfirm(true)}
            disabled={!principalInput.trim() || isExporting}
          >
            {isExporting ? DATA_RIGHTS_COPY.exportLoading : DATA_RIGHTS_COPY.exportButton}
          </button>
          {!principalInput.trim() && !isExporting && (
            <span className="btn-disabled-caption">
              Paste a principal (or resolve one from Sub-panel A) to enable.
            </span>
          )}

          {exportError && <div className="inline-error" style={{ marginTop: 'var(--s-sm)' }}>{exportError}</div>}
        </div>

        {/* ── Sub-panel C: Log privacy event ── */}
        <div className="subpanel">
          <p className="subpanel-title">C — {DATA_RIGHTS_COPY.logTitle}</p>
          <p className="panel-subtitle">{DATA_RIGHTS_COPY.logSubtitle}</p>

          <div className="field" style={{ marginTop: 'var(--s-md)' }}>
            <label className="field-label" htmlFor="dr-event-type">{DATA_RIGHTS_COPY.actionTypeLabel}</label>
            <select
              id="dr-event-type"
              value={eventType}
              onChange={(e) => setEventType(e.target.value as PrivacyActionType)}
            >
              {PRIVACY_ACTIONS.map((key) => (
                <option key={key} value={key}>{PRIVACY_ACTION_LABELS[key]}</option>
              ))}
            </select>
          </div>

          <div className="field">
            <label className="field-label" htmlFor="dr-event-principal">{DATA_RIGHTS_COPY.targetPrincipalLabel}</label>
            <input
              id="dr-event-principal"
              type="text"
              placeholder={DATA_RIGHTS_COPY.principalPlaceholder}
              value={eventPrincipal}
              onChange={(e) => setEventPrincipal(e.target.value)}
            />
            <span className="field-help">{DATA_RIGHTS_COPY.targetPrincipalHelp}</span>
          </div>

          <div className="field">
            <label className="field-label" htmlFor="dr-details">{DATA_RIGHTS_COPY.detailsLabel}</label>
            <textarea
              id="dr-details"
              placeholder={DATA_RIGHTS_COPY.detailsPlaceholder}
              value={eventDetails}
              onChange={(e) => setEventDetails(e.target.value.slice(0, 1000))}
              rows={4}
              style={{
                width: '100%',
                resize: 'vertical',
                fontFamily: 'var(--font-body)',
                fontSize: '14px',
                color: 'var(--ivory)',
                background: 'rgba(0,0,0,0.4)',
                border: '1px solid var(--gold-stroke)',
                padding: '10px 12px',
                lineHeight: '1.5',
              }}
            />
            <span className="field-help" style={{ color: detailsRemaining < 100 ? 'var(--gold-m)' : undefined }}>
              {detailsRemaining} characters remaining.
            </span>
          </div>

          <button
            className="btn"
            onClick={handleLogEvent}
            disabled={!eventDetails.trim() || isLoggingEvent}
          >
            {isLoggingEvent ? DATA_RIGHTS_COPY.logLoading : DATA_RIGHTS_COPY.logButton}
          </button>
          {!eventDetails.trim() && !isLoggingEvent && (
            <span className="btn-disabled-caption">
              Enter details describing the event to enable.
            </span>
          )}

          {logError && <div className="inline-error" style={{ marginTop: 'var(--s-sm)' }}>{logError}</div>}
        </div>
      </div>

      {/* Export confirmation modal */}
      {showExportConfirm && (
        <ConfirmModal
          title="Export User Data"
          body={`Export all data for ${principalInput.trim().slice(0, 12)}…? The bundle will download as JSON. An audit log entry will be created.`}
          confirmLabel="Export"
          isLoading={isExporting}
          onConfirm={handleExportConfirm}
          onCancel={() => setShowExportConfirm(false)}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Admin Allowlist View Panel (read-only)
// ---------------------------------------------------------------------------

function AdminAllowlistPanel({ identity }: { identity: Identity | null }) {
  const [admins, setAdmins] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchAdmins = useCallback(async () => {
    if (!identity) return;
    setIsLoading(true);
    setError(null);
    try {
      const agent = await createAdminAgent(identity);
      const actor = createAdminActor(agent);
      const principals = await actor.get_admins();
      setAdmins(principals.map((p) => p.toText()));
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to fetch admin list.';
      setError(msg);
    } finally {
      setIsLoading(false);
    }
  }, [identity]);

  React.useEffect(() => {
    fetchAdmins();
  }, [fetchAdmins]);

  return (
    <div className="panel">
      <p className="panel-title">Admin Allowlist</p>
      <p className="panel-subtitle">
        Principals authorised to call admin endpoints. Read-only here; mutate via dfx.
      </p>

      {isLoading && <p className="card-caption">Loading.</p>}
      {error && <div className="inline-error">{error}</div>}

      {admins.length === 0 && !isLoading ? (
        <p className="card-caption">No admins on record. Run init-admin.sh after deploy.</p>
      ) : (
        <ul className="allowlist-rows">
          {admins.map((p) => (
            <li key={p} className="allowlist-row">
              <span className="allowlist-principal">{p}</span>
              <button
                className="btn btn-secondary btn-sm"
                disabled
                title="Allowlist additions/removals via dfx only. UI is read-only for safety."
              >
                Remove
              </button>
            </li>
          ))}
        </ul>
      )}

      <p className="card-caption" style={{ marginTop: 'var(--s-md)' }}>
        Allowlist additions/removals via dfx only. UI is read-only for safety.
      </p>
      <a
        href="https://github.com/jerrydorisca/dominus#admin-setup"
        className="btn btn-secondary"
        target="_blank"
        rel="noopener noreferrer"
        style={{ marginTop: 'var(--s-sm)', display: 'inline-flex' }}
      >
        View dfx Procedure
      </a>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Stale Rooms Panel
// ---------------------------------------------------------------------------
//
// Live-canister-backed (2026-05-22). Calls dominus_admin.list_stale_rooms
// at the 30-minute threshold and routes destructive cleanup through the
// existing lobby_canister.admin_force_finish_room method.

function fmtAge(ageMinutes: number): string {
  if (ageMinutes < 60) return `${ageMinutes} min`;
  const h = Math.floor(ageMinutes / 60);
  const m = ageMinutes % 60;
  return m === 0 ? `${h} hr` : `${h} hr ${m} min`;
}

function StaleRoomsPanel({ identity }: { identity: Identity | null }): React.ReactElement {
  const { rooms, isLoading, error, refresh, forceClose, bulkCleanup } = useStaleRooms(identity);
  const { messages, toast, dismiss } = useToast();
  const [confirmClose, setConfirmClose] = useState<StaleRoomInfo | null>(null);
  const [confirmBulk, setConfirmBulk] = useState(false);

  const handleForceClose = (room: StaleRoomInfo) => {
    setConfirmClose(room);
  };

  const handleForceCloseConfirm = async () => {
    if (!confirmClose) return;
    const id = confirmClose.roomId;
    setConfirmClose(null);
    try {
      await forceClose(id);
      toast('ok', `Force-closed room ${id.slice(0, 14)}. Audit log entry recorded.`);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Force-close failed.';
      toast('err', msg);
    }
  };

  const handleBulkCleanup = () => {
    setConfirmBulk(true);
  };

  const handleBulkConfirm = async () => {
    const count = rooms.length;
    setConfirmBulk(false);
    try {
      await bulkCleanup();
      toast('ok', `${count} stale room${count !== 1 ? 's' : ''} marked finished. Audit log entries recorded.`);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Bulk cleanup failed.';
      toast('err', msg);
    }
  };

  const waitingCount = rooms.filter((r) => r.status === 'waiting').length;
  const playingCount = rooms.filter((r) => r.status === 'playing').length;

  return (
    <div className="panel" style={{ gridColumn: '1 / -1' }}>
      <ToastHost messages={messages} onDismiss={dismiss} />

      {error && (
        <p className="panel-subtitle" role="alert" style={{ color: 'var(--color-danger-muted, var(--ivory-d))', marginBottom: 'var(--s-sm)' }}>
          {error}
        </p>
      )}

      <div className="stale-rooms-header">
        <div>
          <p className="panel-title">Stale Rooms</p>
          <p className="panel-subtitle" style={{ marginBottom: 0 }}>
            Orphaned lobby rooms that have exceeded their expected lifespan.
            Force-close individual rooms or prune all at once.
          </p>
          {rooms.length > 0 && (
            <p className="stale-rooms-count" style={{ marginTop: 'var(--s-sm)' }}>
              {rooms.length} room{rooms.length !== 1 ? 's' : ''}
              {' '}·{' '}
              {waitingCount} waiting · {playingCount} playing
            </p>
          )}
        </div>

        <div className="stale-rooms-actions">
          <button
            className="btn btn-secondary btn-sm"
            onClick={refresh}
            disabled={isLoading}
          >
            {isLoading ? 'Refreshing' : 'Refresh'}
          </button>
          <button
            className="btn btn-sm"
            onClick={handleBulkCleanup}
            disabled={rooms.length === 0 || isLoading}
            style={{ borderColor: 'var(--gold-m)', color: 'var(--gold-m)' }}
          >
            Cleanup All ({rooms.length})
          </button>
        </div>
      </div>

      <div className="table-wrap">
        {isLoading ? (
          <div className="stale-rooms-loading">Retrieving stale rooms.</div>
        ) : rooms.length === 0 ? (
          <div className="stale-rooms-empty">
            <p>No stale rooms. The lobby is clear.</p>
          </div>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Room ID</th>
                <th>Host</th>
                <th>Status</th>
                <th>Age</th>
                <th>Players</th>
                <th>Visibility</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {rooms.map((room) => (
                <tr key={room.roomId}>
                  <td>
                    <span className="mono">{room.roomId.slice(0, 14)}…</span>
                  </td>
                  <td>
                    <span style={{ display: 'block', fontStyle: 'normal', fontFamily: 'var(--font-ui)', fontSize: '13px', letterSpacing: '0.06em', color: 'var(--ivory)' }}>
                      {room.hostName}
                    </span>
                    <span className="mono">{room.hostPrincipal.slice(0, 8)}…</span>
                  </td>
                  <td>
                    <span className={`pill is-${room.status}`}>{room.status}</span>
                  </td>
                  <td>{fmtAge(room.ageMinutes)}</td>
                  <td>{room.playerCount} / 4</td>
                  <td>
                    <span className={`pill is-${room.visibility}`}>{room.visibility}</span>
                  </td>
                  <td>
                    <button
                      className="btn btn-secondary btn-sm"
                      onClick={() => handleForceClose(room)}
                      style={{ borderColor: 'var(--gold-m)', color: 'var(--gold-m)', whiteSpace: 'nowrap' }}
                    >
                      Force Close
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Per-row force-close confirmation */}
      {confirmClose && (
        <ConfirmModal
          title="Force-Close Room"
          body={`Force-close room ${confirmClose.roomId.slice(0, 14)}… hosted by ${confirmClose.hostName}? Players in the room will be disconnected and the room marked finished.`}
          confirmLabel="Force Close"
          isDestructive
          onConfirm={handleForceCloseConfirm}
          onCancel={() => setConfirmClose(null)}
        />
      )}

      {/* Bulk cleanup confirmation */}
      {confirmBulk && (
        <ConfirmModal
          title="Cleanup All Stale Rooms"
          body={`Force-close all ${rooms.length} listed room${rooms.length !== 1 ? 's' : ''}? Each room will be marked finished. All players in active rooms will be disconnected. This action is logged for each room.`}
          confirmLabel={`Close ${rooms.length} Room${rooms.length !== 1 ? 's' : ''}`}
          isDestructive
          onConfirm={handleBulkConfirm}
          onCancel={() => setConfirmBulk(false)}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// ControlsPage root
// ---------------------------------------------------------------------------

export default function ControlsPage({ identity }: ControlsPageProps): React.ReactElement {
  return (
    <section className="section" id="controls" aria-labelledby="controls-title">
      <header className="section-header">
        <span className="section-eyebrow">Section VIII</span>
        <h2 className="section-title" id="controls-title">Controls</h2>
        <p className="section-lede">
          Manual interventions. Every action is recorded in the Audit Log.
        </p>
      </header>

      <div className="controls-grid">
        <AutoTopUpPanel identity={identity} />
        <ManualTopUpPanel identity={identity} />
        <IcpTopUpPanel identity={identity} />
        <ForceRenamePanel identity={identity} />
        <BannedWordsPanel identity={identity} />
        <ForceFinishRoomPanel identity={identity} />
        <SubscriptionOverridePanel identity={identity} />
        <DataRightsPanel identity={identity} />
        <AdminAllowlistPanel identity={identity} />
        <StaleRoomsPanel identity={identity} />
      </div>
    </section>
  );
}
